import React, { useCallback, useEffect,useState } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { Button, CircularProgress, Paper, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RefreshButton } from 'common/components/GridBar/RefreshButton';
import { TableWrapper } from 'common/components/GridBar/TableWrapper';
import { TooltipIcon } from 'common/components/TooltipIcon';
import { DJ_ACCOUNT_LINKING_FAILURES_DOCUMENTATION_TOOLTIP, DJ_ACCOUNT_LINKING_FAILURES_DOCUMENTATION_URL } from 'common/constants/admin';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchStepFunctionRunningList } from 'common/services/api/StepFunctions/StepFunctions.thunks';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { clearState,selectPage, selectRowsByDateDescending, setPage, setRows } from '../accountLinkingDashboardSlice';
import { StyledAccountLinkingDashboardTable } from '../StyledAccountLinkingDashboardTable';

import { AccountLinkingDashboardRow } from './AccountLinkingDashboardRow';

export function AccountLinkingDashboardTable() {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const page = useAppSelector(selectPage);
    const rows = useAppSelector(selectRowsByDateDescending) ?? [];
    const [processing, setProcessing] = useState<boolean>(true);
    const [refreshAbortController] = useState(new AbortController());
    const rowsPerPage = 10;

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        dispatch(setPage(newPage));
    };


    const fetchStepFunctionRunningListData = useCallback((signal?: AbortSignal) => {
        setProcessing(true);
        dispatch(fetchStepFunctionRunningList({ signal }))
            .unwrap()
            .then((data) => {
                dispatch(setRows(data));
                setProcessing(false);
            })
            .catch((error) => {
                if (!signal?.aborted) {
                    dispatch(setRows([]));
                    handleListFetchError(extractStatusCode(error.message), 'Step function running list', enqueueSnackbar);
                    setProcessing(false);
                }
            });
    }, []);

    useEffect(() => {
        const abortController = new AbortController();
        const { signal } = abortController;

        dispatch(clearState());
        fetchStepFunctionRunningListData(signal);
        return () => {
            abortController.abort();
            // dashboard refreshes are tracked by a different signal
            refreshAbortController.abort();

            dispatch(clearState());
        };
    }, []);

    const refreshButtonHandler = () => {
        dispatch(clearState());
        dispatch(setPage(0));
        fetchStepFunctionRunningListData(refreshAbortController.signal);
    };

    function getTableContents() {
        if (processing) {
            return (
                <TableRow>
                    <TableCell colSpan={6} align="center">
                        <CircularProgress/>
                    </TableCell>
                </TableRow>
            );
        } else if (rows.length > 0) {
            return (
                <>
                    {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                        <AccountLinkingDashboardRow
                            key={row.executionId.split(':')[1]}
                            row={row}
                        />
                    ))}
                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows + emptyRows }}>
                            <TableCell colSpan={6} />
                        </TableRow>
                    )}
                </>
            );
        } else {
            return (
                <TableRow>
                    <TableCell colSpan={6} align="center">
                        <Typography>No rows</Typography>
                    </TableCell>
                </TableRow>
            );
        }
    }

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    return (
        <TableContainer
            component={Paper}
            sx={{ display:'flex', flexDirection:'column', alignItems:'center' }}
            data-testid="dashboard-table"
        >
            <TableWrapper>
                <RefreshButton
                    onClickHandler={refreshButtonHandler}
                    disabled={processing}
                />
                <Button
                    sx={{
                        padding: '4px',
                        minWidth: 'auto',
                        marginRight: '1.5rem',
                        border: null,
                        backgroundColor: 'transparent'
                    }}
                    target='_blank'
                    href={DJ_ACCOUNT_LINKING_FAILURES_DOCUMENTATION_URL}
                    data-testid={'documentation-url'}
                >
                    <TooltipIcon
                        IconComponent={InfoIcon}
                        tooltipTitle={DJ_ACCOUNT_LINKING_FAILURES_DOCUMENTATION_TOOLTIP}
                        iconColor='primary'
                        iconInlineStyles={{ color: theme.palette.primary.accent as string }}
                    />
                </Button>
            </TableWrapper>
            <StyledAccountLinkingDashboardTable aria-label="collapsible table">
                <TableHead>
                    <TableRow>
                        <TableCell width='5%'/>
                        <TableCell width='20%'>Execution Id</TableCell>
                        <TableCell width='12.5%'>Execution Datetime</TableCell>
                        <TableCell width='12.5%'>Link Type</TableCell>
                        <TableCell width='42.5%'>Application/Tenant</TableCell>
                        <TableCell
                            className='no-truncate'
                            width='7.5%'>
                                        Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {getTableContents()}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            align="center"
                            rowsPerPageOptions={[5]}
                            colSpan={5}
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                        />
                    </TableRow>
                </TableFooter>
            </StyledAccountLinkingDashboardTable>
        </TableContainer>
    );
}
